import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { BookOpen, Plus, Edit2, Trash2, List, Loader2, AlertCircle, Video, FileText, Link, Upload } from 'lucide-react';

const ProviderCourses = ({ authToken }) => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [lessonsModal, setLessonsModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', short_description: '', category_id: '', thumbnail_path: '', base_price: 0, sale_price: '', status: 'draft' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [commissionRate, setCommissionRate] = useState(0);

  const fetchCourses = () => {
    fetch(`${API_BASE_URL}/admin/courses`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then((data) => setCourses(data.courses || []))
      .catch((err) => setError(err.message || err.error || 'Failed to load courses'));
  };

  const fetchCategories = () => {
    fetch(`${API_BASE_URL}/admin/course-categories`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then((data) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/admin/courses`, { headers: { Authorization: `Bearer ${authToken}` } }).then((r) => r.json()),
      fetch(`${API_BASE_URL}/admin/course-categories`, { headers: { Authorization: `Bearer ${authToken}` } }).then((r) => r.json()),
    ])
      .then(([c, cat]) => {
        setCourses(c.courses || []);
        setCategories(cat.categories || []);
      })
      .catch((err) => setError(err.message || err.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [authToken]);

  useEffect(() => {
    if (form.category_id) {
      fetch(`${API_BASE_URL}/admin/course-categories/${form.category_id}/commission`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(res => res.ok ? res.json() : Promise.reject('Failed'))
        .then(data => {
          if (data && data.data && data.data.commission_percentage !== undefined) {
            setCommissionRate(data.data.commission_percentage);
          } else {
            setCommissionRate(0);
          }
        })
        .catch(() => setCommissionRate(0));
    } else {
      setCommissionRate(0);
    }
  }, [form.category_id, authToken]);

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('thumbnail', file);

    fetch(`${API_BASE_URL}/admin/courses/thumbnail-upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then((data) => {
        setForm((prev) => ({ ...prev, thumbnail_path: data.thumbnail_path }));
      })
      .catch((err) => setError(err.error || 'Upload failed'))
      .finally(() => setUploading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const url = modal?.course_id ? `${API_BASE_URL}/admin/courses/${modal.course_id}` : `${API_BASE_URL}/admin/courses`;
    const method = modal?.course_id ? 'PUT' : 'POST';

    // Ensure numeric types and nulls for optional fields
    const finalPrice = ((parseFloat(form.base_price) || 0) + ((parseFloat(form.base_price) || 0) * (commissionRate / 100)));
    const payload = {
      ...form,
      category_id: form.category_id ? parseInt(form.category_id, 10) : null,
      base_price: parseFloat(form.base_price) || 0,
      sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
      price: finalPrice
    };

    fetch(url, {
      method,
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => { setModal(null); setForm({ name: '', description: '', short_description: '', category_id: '', base_price: 0, sale_price: '', status: 'draft' }); fetchCourses(); })
      .catch((err) => setError(err.message || err.error || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const openEdit = (course) => {
    setModal(course);
    setForm({
      name: course.name || '', description: course.description || '', short_description: course.short_description || '',
      category_id: course.category_id ?? '', thumbnail_path: course.thumbnail_path || '',
      base_price: course.base_price ?? 0, sale_price: course.sale_price ?? '', status: course.status || 'draft',
    });
  };

  const deleteCourse = (course_id) => {
    if (!window.confirm('Delete this course?')) return;
    fetch(`${API_BASE_URL}/admin/courses/${course_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => { setModal(null); setLessonsModal(null); fetchCourses(); })
      .catch((err) => setError(err.message || err.error || 'Delete failed'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">My Courses</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => { setModal({}); setForm({ name: '', description: '', short_description: '', category_id: '', base_price: 0, sale_price: '', status: 'draft' }); }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Course
        </button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {courses.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No courses yet.</td>
              </tr>
            ) : (
              courses.map((c) => (
                <tr key={c.course_id}>
                  <td className="px-4 py-3 text-sm text-gray-900">{c.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {categories.find((cat) => cat.id === c.category_id)?.name || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">₹{c.base_price}{c.sale_price ? ` (₹${c.sale_price})` : ''}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-800' : c.status === 'draft' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => setLessonsModal(c)} className="text-blue-600 hover:underline mr-2">
                      <List className="h-4 w-4 inline mr-1" /> Episodes
                    </button>
                    <button type="button" onClick={() => openEdit(c)} className="text-indigo-600 hover:underline mr-2">Edit</button>
                    <button type="button" onClick={() => deleteCourse(c.course_id)} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">{modal.course_id ? 'Edit Course' : 'Add Course'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-3">
                  <input type="text" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2" required />
                  <textarea placeholder="Short description" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
                  <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded px-3 py-2" rows={3} />

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Course Thumbnail</label>
                    <div className="flex items-center gap-4">
                      {form.thumbnail_path && (
                        <img
                          src={`${API_BASE_URL.replace('/api', '')}${form.thumbnail_path}`}
                          alt="Thumbnail preview"
                          className="w-16 h-16 object-cover rounded border"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 border rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        <span className="text-sm">{form.thumbnail_path ? 'Change Image' : 'Upload Image'}</span>
                        <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
                      </label>
                    </div>
                    {form.thumbnail_path && (
                      <p className="text-xs text-gray-500 truncate">{form.thumbnail_path}</p>
                    )}
                  </div>

                  <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full border rounded px-3 py-2">
                    <option value="">No category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <div>
                    <input type="number" step="0.01" placeholder="Base price" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} className="w-full border rounded px-3 py-2" />
                    {form.category_id && commissionRate > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        + {commissionRate}% platform commission. Final price (Base + Commission): ₹{((parseFloat(form.base_price) || 0) + ((parseFloat(form.base_price) || 0) * (commissionRate / 100))).toFixed(2)}
                      </p>
                    )}
                  </div>
                  <input type="number" step="0.01" placeholder="Sale price (optional)" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} className="w-full border rounded px-3 py-2" />
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded px-3 py-2">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {lessonsModal && (
        <LessonsModal course={lessonsModal} authToken={authToken} onClose={() => setLessonsModal(null)} />
      )}
    </div>
  );
};

function LessonsModal({ course, authToken, onClose }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title: '', sort_order: 0, content_type: 'youtube', youtube_url: '', pdf_path: '', link_url: '', duration_minutes: '', is_free_preview: false });
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [error, setError] = useState('');

  const fetchLessons = () => {
    setError('');
    fetch(`${API_BASE_URL}/admin/courses/${course.course_id}/lessons`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then((data) => setLessons(data.lessons || []))
      .catch((err) => setError(err.message || err.error || 'Failed to load episodes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchLessons();
  }, [course?.course_id, authToken]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const url = modal?.lesson_id
      ? `${API_BASE_URL}/admin/courses/${course.course_id}/lessons/${modal.lesson_id}`
      : `${API_BASE_URL}/admin/courses/${course.course_id}/lessons`;
    const method = modal?.lesson_id ? 'PUT' : 'POST';

    // Align with API fields
    const payload = {
      title: form.title,
      sort_order: parseInt(form.sort_order, 10) || 0,
      content_type: form.content_type || 'youtube',
      youtube_url: form.content_type === 'youtube' ? form.youtube_url : null,
      pdf_path: form.content_type === 'pdf' ? form.pdf_path : null,
      link_url: form.content_type === 'link' ? form.link_url : null,
      duration_minutes: parseInt(form.duration_minutes, 10) || 0,
      is_free_preview: !!form.is_free_preview
    };

    fetch(url, {
      method,
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => { setModal(null); setForm({ title: '', sort_order: 0, content_type: 'youtube', youtube_url: '', pdf_path: '', link_url: '', duration_minutes: '', is_free_preview: false }); fetchLessons(); })
      .catch((err) => setError(err.message || err.error || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const openEdit = (les) => {
    setModal(les);
    setForm({
      title: les.title || '', sort_order: les.sort_order ?? 0, content_type: les.content_type || 'youtube',
      youtube_url: les.youtube_url || '', pdf_path: les.pdf_path || les.pdf_url || '', link_url: les.link_url || '',
      duration_minutes: les.duration_minutes ?? '', is_free_preview: !!les.is_free_preview,
    });
  };

  const deleteLesson = (lesson_id) => {
    if (!window.confirm('Delete this episode?')) return;
    setError('');
    fetch(`${API_BASE_URL}/admin/courses/${course.course_id}/lessons/${lesson_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => {
        if (modal?.lesson_id === lesson_id) setModal(null);
        fetchLessons();
      })
      .catch((err) => setError(err.message || err.error || 'Delete failed'));
  };

  // POST /admin/courses/<course_id>/lessons/pdf-upload — multipart/form-data key: pdf_file; response 201: { message, pdf_path }
  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file.');
      return;
    }
    setError('');
    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('pdf_file', file);

    fetch(`${API_BASE_URL}/admin/courses/${course.course_id}/lessons/pdf-upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    })
      .then(async (res) => {
        const text = await res.text();
        let data;
        try {
          data = text.length ? JSON.parse(text) : {};
        } catch (_) {
          if (!res.ok) {
            if (text.toLowerCase().includes('<!doctype') || text.trim().startsWith('<')) {
              throw new Error('Server returned an error page. The PDF upload endpoint may not be configured or the request was rejected. Please try again or use "Or paste path/URL" for a direct path.');
            }
            throw new Error(res.status === 404 ? 'PDF upload endpoint not found. Please ensure the backend supports POST /admin/courses/<course_id>/lessons/pdf-upload.' : `Upload failed (${res.status}).`);
          }
          throw new Error('Invalid response from server. Please try "Or paste path/URL" to enter a PDF path manually.');
        }
        if (!res.ok) throw Object.assign(new Error(data.error || data.message || 'Upload failed'), { error: data.error, message: data.message });
        return data;
      })
      .then((data) => {
        const path = data.pdf_path || data.path;
        if (path) setForm((prev) => ({ ...prev, pdf_path: path }));
      })
      .catch((err) => setError(err?.error ?? err?.message ?? 'PDF upload failed'))
      .finally(() => { setUploadingPdf(false); e.target.value = ''; });
  };

  const contentTypeIcon = (t) => {
    if (t === 'youtube') return <Video className="h-4 w-4 text-red-500" />;
    if (t === 'pdf') return <FileText className="h-4 w-4 text-blue-600" />;
    return <Link className="h-4 w-4 text-green-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-2">Episodes: {course.name}</h3>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 mr-2" />
              {error}
            </div>
          )}
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <>
              <div className="mb-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => { setModal({}); setForm({ title: '', sort_order: lessons.length, content_type: 'youtube', youtube_url: '', pdf_path: '', link_url: '', duration_minutes: '', is_free_preview: false }); }}
                  className="text-sm px-3 py-1 bg-blue-600 text-white rounded"
                >
                  + Add Episode
                </button>
              </div>
              <ul className="space-y-2 mb-4">
                {lessons.map((les) => (
                  <li key={les.lesson_id} className="flex items-center justify-between border-b pb-2">
                    <span className="flex items-center gap-2">
                      {contentTypeIcon(les.content_type)}
                      {les.title} ({les.content_type})
                    </span>
                    <span>
                      <button type="button" onClick={() => openEdit(les)} className="text-indigo-600 text-sm mr-2">Edit</button>
                      <button type="button" onClick={() => deleteLesson(les.lesson_id)} className="text-red-600 text-sm">Delete</button>
                    </span>
                  </li>
                ))}
              </ul>
              {modal && (
                <form onSubmit={handleSubmit} className="border-t pt-4 space-y-2">
                  <input type="text" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded px-2 py-1" required />
                  <input type="number" placeholder="Order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} className="w-full border rounded px-2 py-1" />
                  <select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} className="w-full border rounded px-2 py-1">
                    <option value="youtube">YouTube</option>
                    <option value="pdf">PDF</option>
                    <option value="link">Link</option>
                  </select>
                  {form.content_type === 'youtube' && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500">YouTube Video URL</label>
                      <input type="text" placeholder="https://www.youtube.com/watch?v=..." value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="w-full border rounded px-2 py-1" />
                    </div>
                  )}
                  {form.content_type === 'pdf' && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500">PDF Document</label>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-200 transition-colors w-fit">
                          {uploadingPdf ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Upload className="h-4 w-4 text-gray-600" />}
                          <span className="text-sm">{form.pdf_path ? 'Change PDF' : 'Upload PDF'}</span>
                          <input type="file" accept=".pdf,application/pdf" onChange={handlePdfUpload} className="hidden" disabled={uploadingPdf} />
                        </label>
                        {form.pdf_path && (
                          <p className="text-xs text-gray-600 truncate" title={form.pdf_path}>Uploaded: {form.pdf_path.split('/').pop() || form.pdf_path}</p>
                        )}
                        <input type="text" placeholder="Or paste path/URL (optional)" value={form.pdf_path} onChange={(e) => setForm({ ...form, pdf_path: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                      </div>
                    </div>
                  )}
                  {form.content_type === 'link' && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500">Resource Link URL</label>
                      <input type="url" placeholder="https://..." value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} className="w-full border rounded px-2 py-1" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500">Duration (Min)</label>
                      <input type="number" placeholder="10" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full border rounded px-2 py-1" />
                    </div>
                    <div className="flex items-center pt-5">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" checked={form.is_free_preview} onChange={(e) => setForm({ ...form, is_free_preview: e.target.checked })} />
                        Free preview
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setModal(null)} className="px-3 py-1 border rounded">Cancel</button>
                    <button type="submit" disabled={saving} className="px-3 py-1 bg-blue-600 text-white rounded">Save</button>
                  </div>
                </form>
              )}
            </>
          )}
          <button type="button" onClick={onClose} className="mt-4 w-full py-2 border rounded">Close</button>
        </div>
      </div>
    </div>
  );
}

export default ProviderCourses;
